import { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  Calendar,
  Search,
  Mail,
  Phone,
  Send,
  Clock,
  AlertCircle,
  CheckCircle2,
  User,
  ChevronRight,
  RefreshCcw,
  ShieldAlert,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAvatarUrl } from '../../utils/avatar';
import Skeleton from '../../Components/common/Skeleton';

const AvailabilityForecast = () => {
  const [forecast, setForecast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, expiring, free_soon, available
  const [sendingDoctorId, setSendingDoctorId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchForecast();
  }, []);

  const fetchForecast = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await api.get('/doctor-availability/forecast');
      if (response.data.success) {
        setForecast(response.data.data);
      }
    } catch (err) {
      if (!silent) toast.error('Failed to load roster predictions');
      console.error(err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleSendInvite = async (doctorId, doctorName) => {
    setSendingDoctorId(doctorId);
    try {
      const response = await api.post('/doctor-availability/request', { doctorId });
      if (response.data.success) {
        toast.success(`Availability roster invite sent to ${doctorName}!`);
        // Refresh forecast to show updated status
        await fetchForecast(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to send invite to ${doctorName}`);
    } finally {
      setSendingDoctorId(null);
    }
  };

  const filteredForecast = useMemo(() => {
    return forecast.filter(item => {
      // Search matches
      const matchesSearch = item.doctorName.toLowerCase().includes(search.toLowerCase()) ||
        item.specialization?.toLowerCase().includes(search.toLowerCase());

      if (!matchesSearch) return false;

      // Status filters
      if (filterStatus === 'expiring') {
        return item.needsRosterUpdate;
      }
      if (filterStatus === 'free_soon') {
        return item.daysUntilFree > 0 && item.daysUntilFree <= 2;
      }
      if (filterStatus === 'available') {
        return item.daysUntilFree === 0 && !item.needsRosterUpdate;
      }

      return true;
    });
  }, [forecast, search, filterStatus]);

  if (loading) {
    return (
      <div className="space-y-8 pb-20">
        {/* HEADER SECTION SKELETON */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white rounded-[32px] sm:rounded-[40px] p-8 sm:p-10 border border-slate-100 shadow-sm relative overflow-hidden">
          <div className="space-y-3 flex-1 w-full">
            <div className="flex items-center gap-2">
              <Skeleton className="w-8 h-8 rounded-xl" />
              <Skeleton className="h-3 w-28 rounded-lg" />
            </div>
            <Skeleton className="h-9 w-64 rounded-xl" />
            <Skeleton className="h-3 w-80 rounded-lg" />
          </div>
          <Skeleton className="h-12 w-44 rounded-2xl shrink-0" />
        </div>

        {/* ANNOUNCEMENT BANNER SKELETON */}
        <div className="bg-slate-50 border border-slate-100 rounded-3xl p-6 sm:p-8 flex items-start gap-4">
          <Skeleton className="w-6 h-6 rounded-full shrink-0" />
          <div className="space-y-2 flex-1">
            <Skeleton className="h-4 w-48 rounded-lg" />
            <Skeleton className="h-3 w-full rounded-lg" />
            <Skeleton className="h-3 w-5/6 rounded-lg" />
          </div>
        </div>

        {/* FILTER PANEL SKELETON */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center bg-white rounded-3xl p-5 border border-slate-100 shadow-sm">
          <div className="lg:col-span-6">
            <Skeleton className="h-12 w-full rounded-2xl" />
          </div>
          <div className="lg:col-span-6 flex flex-wrap gap-2 justify-start lg:justify-end">
            {Array(4).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-10 w-28 rounded-xl" />
            ))}
          </div>
        </div>

        {/* FORECAST GRID SKELETON */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array(3).fill(0).map((_, idx) => (
            <div key={idx} className="bg-white rounded-[2.5rem] border border-slate-100 p-6 flex flex-col justify-between shadow-sm space-y-6">
              <div>
                {/* Profile Header */}
                <div className="flex items-start gap-4 mb-5">
                  <Skeleton className="w-14 h-14 rounded-2xl shrink-0" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-5 w-3/4 rounded-lg" />
                    <Skeleton className="h-3 w-1/2 rounded-lg" />
                    <Skeleton className="h-2 w-1/3 rounded-lg" />
                  </div>
                </div>

                {/* Status Badge */}
                <div className="mb-5 bg-slate-50 p-4 rounded-3xl border border-slate-100 flex justify-between items-center">
                  <Skeleton className="h-3 w-24 rounded-lg" />
                  <Skeleton className="h-4 w-32 rounded-lg" />
                </div>

                {/* Warning / Action */}
                <div className="mb-6 bg-slate-50 border border-slate-100 p-4 rounded-3xl flex items-start gap-3">
                  <Skeleton className="w-5 h-5 rounded-full shrink-0" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-3 w-20 rounded-lg" />
                    <Skeleton className="h-3.5 w-full rounded-lg" />
                  </div>
                </div>

                {/* Operational Dates */}
                <div className="space-y-2 px-4 py-3 bg-slate-50/50 rounded-2xl border border-slate-100/50 mb-6">
                  <div className="flex justify-between items-center">
                    <Skeleton className="h-2.5 w-24 rounded-lg" />
                    <Skeleton className="h-2.5 w-20 rounded-lg" />
                  </div>
                  <div className="flex justify-between items-center">
                    <Skeleton className="h-2.5 w-36 rounded-lg" />
                    <Skeleton className="h-2.5 w-20 rounded-lg" />
                  </div>
                </div>

                {/* Direct Contact Links */}
                <div className="space-y-2 mb-6 px-1">
                  <Skeleton className="h-10 w-full rounded-2xl" />
                  <Skeleton className="h-10 w-full rounded-2xl" />
                </div>
              </div>

              {/* Action Button */}
              <Skeleton className="h-12 w-full rounded-2xl" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">

      {/* HEADER SECTION */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white rounded-[32px] sm:rounded-[40px] p-8 sm:p-10 border border-gray-100 shadow-xl shadow-gray-100/40">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
              <Calendar className="w-4 h-4" />
            </div>
            <span className="text-[9px] font-black uppercase text-blue-500 tracking-[0.2.em]">Operations Hub</span>
          </div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">Update Availability</h1>
          <p className="text-gray-400 font-bold uppercase text-[9px] tracking-widest mt-1">Real-time Predictability & email dispatch center for CC / Admin</p>
        </div>
        <button
          onClick={fetchForecast}
          className="flex items-center gap-2 px-5 py-3.5 bg-gray-50 border border-gray-200 hover:bg-gray-100 font-bold rounded-2xl text-xs uppercase tracking-wider text-slate-700 active:scale-95 transition-all shadow-sm shrink-0"
        >
          <RefreshCcw className="w-3.5 h-3.5" /> Refresh Schedule
        </button>
      </div>

      {/* ANNOUNCEMENT BANNER */}
      <div className="bg-gradient-to-r from-blue-50 to-[#EFF6FF] border border-blue-100 rounded-3xl p-6 sm:p-8 flex flex-col sm:flex-row items-start gap-4">
        <AlertCircle className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
        <div className="space-y-1.5 flex-1">
          <h4 className="text-sm font-black text-blue-900 uppercase tracking-widest">Availability Update (2-Day Pre-Alert)</h4>
          <p className="text-xs text-blue-700/80 font-semibold leading-relaxed">
            The platform alerts you immediately when a doctor's availability schedule is expiring in <b>2 days or less</b> (marked as <span className="text-amber-600 font-black">Needs Roster Update</span>). Send them a secure availability scheduler invitation email directly by clicking the <b>Send Roster Invitation</b> button on their card.
          </p>
        </div>
      </div>

      {/* FILTER PANEL */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center bg-white rounded-3xl p-5 border border-gray-100 shadow-lg shadow-gray-100/20">
        <div className="lg:col-span-6 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search doctors by name, specialty branches..."
            className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-transparent rounded-2xl text-xs sm:text-sm font-bold text-gray-700 outline-none focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-50 transition-all"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="lg:col-span-6 flex flex-wrap gap-2 justify-start lg:justify-end">
          <FilterButton active={filterStatus === 'all'} label="All Specialists" count={forecast.length} onClick={() => setFilterStatus('all')} />
          <FilterButton active={filterStatus === 'expiring'} label="Needs Roster Update" count={forecast.filter(i => i.needsRosterUpdate).length} color="amber" onClick={() => setFilterStatus('expiring')} />
          <FilterButton active={filterStatus === 'free_soon'} label="Free Soon (1-2 days)" count={forecast.filter(i => i.daysUntilFree > 0 && i.daysUntilFree <= 2).length} color="indigo" onClick={() => setFilterStatus('free_soon')} />
          <FilterButton active={filterStatus === 'available'} label="Available Now" count={forecast.filter(i => i.daysUntilFree === 0 && !i.needsRosterUpdate).length} color="emerald" onClick={() => setFilterStatus('available')} />
        </div>
      </div>

      {/* FORECAST GRID */}
      {filteredForecast.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredForecast.map((item) => (
            <div
              key={item.doctorId}
              className={`bg-white rounded-[2.5rem] border p-6 flex flex-col justify-between shadow-md hover:shadow-xl transition-all relative overflow-hidden group ${item.needsRosterUpdate ? 'border-amber-200 ring-2 ring-amber-50' : 'border-slate-100 hover:border-blue-200'
                }`}
            >
              <div>
                {/* Visual Glow for Warning items */}
                {item.needsRosterUpdate && (
                  <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-2xl -mr-10 -mt-10"></div>
                )}

                {/* Profile Header */}
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl flex items-center justify-center text-[#143E78] font-black text-lg shadow-inner overflow-hidden shrink-0 border border-slate-100">
                    {(() => {
                      const avatarUrl = item.weeklySchedule?.find(d => d.type === 'PHOTO')?.url;
                      if (avatarUrl) {
                        return <img src={getAvatarUrl(avatarUrl)} alt="Profile" className="w-full h-full object-cover" />;
                      }
                      return <User className="w-6 h-6 text-[#143E78]/50" />;
                    })()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4
                      onClick={() => navigate(`/doctors/${item.doctorId}`)}
                      className="font-black text-[#143E78] uppercase tracking-tighter cursor-pointer hover:text-blue-600 truncate flex items-center gap-1 leading-tight text-base sm:text-lg transition-colors"
                    >
                      {item.doctorName}
                    </h4>
                    <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest mt-0.5 truncate">{item.specialization}</p>
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mt-1">{item.doctorType}</span>
                  </div>
                </div>

                {/* 2. Current Availability Status Badge */}
                <div className="mb-5 bg-slate-50 p-4 rounded-3xl border border-slate-100 flex justify-between items-center">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Availability Status</span>
                  <span className={`text-xs font-black uppercase tracking-tight ${item.needsRosterUpdate
                    ? 'text-amber-600'
                    : item.daysUntilFree === 0
                      ? 'text-emerald-600'
                      : 'text-indigo-600'
                    }`}>
                    {item.needsRosterUpdate
                      ? 'Roster Not Submitted'
                      : item.daysUntilFree === 0
                        ? 'Available Today'
                        : `Available in ${item.daysUntilFree} Days`}
                  </span>
                </div>

                {/* 3. 🎯 WHAT IS REQUIRED / ACTION REQUIRED (Super Direct & Simple for Managers) */}
                <div className="mb-6">
                  {item.needsRosterUpdate ? (
                    <div className="bg-amber-50 border border-amber-200 p-4 rounded-3xl flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[10px] font-black text-amber-800 uppercase tracking-widest leading-none">⚠️ Action Required</p>
                        <p className="text-xs text-amber-700 font-extrabold mt-1.5 leading-snug">
                          {!item.expiryDate
                            ? "Weekly Roster Submission is required."
                            : `Roster Calendar Renewal is required (expires in ${item.daysUntilExpiry} days).`}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-3xl flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[10px] font-black text-emerald-800 uppercase tracking-widest leading-none">✅ Fully Operational</p>
                        <p className="text-xs text-emerald-600 font-extrabold mt-1.5 leading-snug">
                          Active roster is submitted and valid for the next {item.daysUntilExpiry} days.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* 4. Operational Dates Detail */}
                <div className="space-y-1.5 px-4 py-3 bg-slate-50/50 rounded-2xl text-[10px] text-slate-500 border border-slate-100/50 mb-6">
                  {item.daysUntilFree > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-slate-400">Available Date:</span>
                      <span className="font-black text-[#143E78]">{new Date(item.nextFreeDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    </div>
                  )}
                  {item.expiryDate && (
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-slate-400">Current Roster Valid Until:</span>
                      <span className="font-black text-slate-700">{new Date(item.expiryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    </div>
                  )}
                </div>

                {/* 5. Direct Contact Links */}
                <div className="space-y-2 mb-6 px-1">
                  <a
                    href={`tel:${item.phone}`}
                    className="flex items-center gap-3 py-2.5 px-4 bg-slate-50 hover:bg-slate-100 border border-slate-100 rounded-2xl text-[11px] text-slate-600 font-bold transition-all"
                  >
                    <Phone className="w-4 h-4 text-slate-400 shrink-0" />
                    <span className="truncate">{item.phone}</span>
                  </a>
                  <a
                    href={`mailto:${item.email}`}
                    className="flex items-center gap-3 py-2.5 px-4 bg-slate-50 hover:bg-slate-100 border border-slate-100 rounded-2xl text-[11px] text-indigo-600 font-bold transition-all"
                    title={item.email}
                  >
                    <Mail className="w-4 h-4 text-indigo-400 shrink-0" />
                    <span className="truncate">{item.email}</span>
                  </a>
                </div>
              </div>

              {/* ── ACTION dispatch button ── */}
              <button
                onClick={() => handleSendInvite(item.doctorId, item.doctorName)}
                disabled={sendingDoctorId === item.doctorId}
                className={`w-full py-4 rounded-2xl flex items-center justify-center gap-2 text-xs font-black uppercase tracking-wider active:scale-[0.98] transition-all shadow-md group-hover:shadow-lg ${item.needsRosterUpdate
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-amber-500/10'
                  : 'bg-gradient-to-r from-[#143E78] to-[#205295] hover:from-[#1b4883] hover:to-[#143E78] text-white shadow-blue-500/10'
                  }`}
              >
                {sendingDoctorId === item.doctorId ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                    Dispatching Invite...
                  </>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5 shrink-0 group-hover:translate-x-0.5 transition-transform" />
                    Send Roster Invitation
                  </>
                )}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-24 text-center bg-white rounded-[32px] border border-gray-100 shadow-sm p-10 flex flex-col items-center justify-center">
          <AlertCircle className="w-12 h-12 text-slate-300 mb-4 animate-bounce" />
          <h3 className="text-lg font-black text-slate-700 uppercase tracking-tight">No Roster Predictions Found</h3>
          <p className="text-slate-400 font-semibold text-xs mt-1 max-w-sm">No clinical partners matched your search query or selected filter criteria right now.</p>
        </div>
      )}

    </div>
  );
};

const FilterButton = ({ active, label, count, color = 'blue', onClick }) => {
  const colorMap = {
    blue: active ? 'bg-[#143E78] text-white shadow-blue-500/10' : 'bg-gray-50 hover:bg-gray-100 text-gray-500 border border-slate-100',
    amber: active ? 'bg-amber-500 text-white shadow-amber-500/10' : 'bg-amber-50/50 hover:bg-amber-50 text-amber-600 border border-amber-100/50',
    indigo: active ? 'bg-indigo-600 text-white shadow-indigo-500/10' : 'bg-indigo-50/50 hover:bg-indigo-50 text-indigo-600 border border-indigo-100/50',
    emerald: active ? 'bg-emerald-600 text-white shadow-emerald-500/10' : 'bg-emerald-50/50 hover:bg-emerald-50 text-emerald-600 border border-emerald-100/50',
  };

  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider flex items-center gap-2 active:scale-95 transition-all shadow-sm ${colorMap[color]}`}
    >
      {label}
      <span className={`px-2 py-0.5 rounded-md text-[9px] font-black ${active ? 'bg-white/20 text-white' : 'bg-slate-200/50 text-slate-600'
        }`}>
        {count}
      </span>
    </button>
  );
};

export default AvailabilityForecast;
